import { formatDate, OUTWARD_CREATION_MIN_DATE, WORK_START_DATE_FIELD } from "../services/dateUtils";
import { Field, PicklistValue } from "../types/field";
import { materialTypeValues } from "./materialInward";
import { locatorTypeValues } from "./locator";
import { outProjectTypeValues } from "./project";
import { senderDocTypeValues, receiverDocTypeValues } from "./materialOutward";
import { Column } from "../types/column";

const transferTransactionTypeValues: PicklistValue[] = [
  { value: "PS", label: "Partner Store Tfr" },
  { value: "CS", label: "Contractor Store Tfr" },
  { value: "IS", label: "Internal Shift" },
];

export const storeTransferDetailFields: Field[] = [
  {
    name: "sno",
    label: "SNo.",
    type: "text",
    autoFilled: true,
    allowNull: true,
    props: {
      widthFactor: 0.5,
    },
  },
  {
    name: "itemName",
    label: "Item Name",
    type: "lookup",
    lookupType: "item",
    lookupCategory: "availableItem",
    props: {
      widthFactor: 3,
    },
    lookupParams: [
      {
        key: "docLocatorCode",
        resolveValue: true,
        valueFieldName: "docLocatorCode",
      },
      {
        key: "materialType",
        resolveValue: true,
        valueFieldName: "docMaterialType",
      },
      {
        key: "projectCode",
        resolveValue: true,
        valueFieldName: "docProjectCode",
      },
      {
        key: "docDate",
        resolveValue: true,
        valueFieldName: "docDate",
      },
      {
        key: "projectType",
        resolveValue: true,
        valueFieldName: "docProjectType",
      },
      {
        key: "ownerCode",
        resolveValue: true,
        valueFieldName: "docOwnerCode",
      },
      {
        key: "usedItemProjectCodes",
        resolveValue: true,
        valueFieldName: "itemProjectCodes",
      },
      { key: "receiverProjectCode", resolveValue: true, valueFieldName: "receiverProjectCode" },
      {
        key: "receiverMaterialType",
        resolveValue: true,
        valueFieldName: "receiverMaterialType",
      },
      {
        key: "transactionType",
        resolveValue: true,
        valueFieldName: "transactionType",
      },
    ],
    dependentFields: [
      {
        additionalFieldName: "details",
        lookupFieldName: "itemCode",
        fieldName: "itemCode",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "itemCustCode",
        fieldName: "itemCustCode",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "itemId",
        fieldName: "itemId",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "uom",
        fieldName: "uom",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "decimalFlg",
        fieldName: "decimalFlg",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "availableQuantity",
        fieldName: "availableQuantity",
      },
    ],
  },
  {
    name: "itemCustCode",
    label: "Part Code",
    type: "text",
    allowNull: true,
    autoFilled: true,
    props: {
      widthFactor: 1.6,
    },
  },
  {
    name: "uom",
    label: "Unit",
    type: "text",
    autoFilled: true,
    props: {
      widthFactor: 0.7,
    },
  },
  {
    name: "projectVirtualLocator",
    label: "Project Virtual Locator",
    type: "lookup",
    defaultValueFieldName: "docVirtualLocator",
    allowUpdate: false,
    allowNull: true,
    lookupType: "project",
    lookupCategory: "outwardProjectVirtual",
    props: {
      widthFactor: 3,
    },
    lookupFilter: {
      type: "AND",
      filters: [
        {
          type: "EQUALS",
          field: "projectType",
          resolveValue: true,
          valueFieldName: "docProjectType",
        },
      ],
    },
    lookupParams: [
      {
        key: "docLocatorCode",
        resolveValue: true,
        valueFieldName: "docLocatorCode",
      },
      {
        key: "projectType",
        resolveValue: true,
        valueFieldName: "docProjectType",
      },
      {
        key: "ownerCode",
        resolveValue: true,
        valueFieldName: "docOwnerCode",
      },
      {
        key: "itemId",
        resolveValue: true,
        valueFieldName: "itemId",
      },
      {
        key: "usedItemProjectCodes",
        resolveValue: true,
        valueFieldName: "itemProjectCodes",
      },
      { key: "receiverProjectCode", resolveValue: true, valueFieldName: "receiverProjectCode" },
      {
        key: "receiverMaterialType",
        resolveValue: true,
        valueFieldName: "receiverMaterialType",
      },
    ],
    dependentFields: [
      {
        lookupFieldName: "id",
        fieldName: "projectCode",
      },
      {
        additionalFieldName: "details",
        lookupFieldName: "totalAvailableQuantity",
        fieldName: "availableQuantity",
      },
    ],
    disability: {
      type: "OR",
      filters: [
        {
          type: "NIN",
          field: "multiProjectFlg",
          values: [true],
        },
        { type: "MISSING", field: "itemName" },
      ],
    },
  },
  {
    name: "projectCode",
    label: "Project",
    type: "text",
    defaultValueFieldName: "docProjectCode",
    allowNull: true,
    autoFilled: true,
    props: {
      widthFactor: 0.9,
    },
  },
  {
    name: "availableQuantity",
    label: "Available Quantity",
    type: "text",
    allowNull: true,
    autoFilled: true,
    hiddenForView: true,
    props: {
      widthFactor: 1,
    },
  },
  {
    name: "totalQuantity",
    label: "Out Quantity",
    type: "number",
    defaultValue: 0,
    allowUpdate: false,
    decimalFlg: true,
    constraints: [{ type: "RANGE", maxValueScript: { valueFields: ["availableQuantity"] }, minLimit: 0 }],
    props: {
      widthFactor: 1,
    },
  },
  {
    name: "totalAmount",
    label: "Total Amount",
    type: "number",
    decimal: true,
    autoFilled: true,
    valueScript: { operation: "multi", valueFields: ["rate", "totalQuantity"] },
    props: {
      widthFactor: 1,
    },
  },
  {
    name: "itemId",
    label: "Item Id",
    type: "text",
    autoFilled: true,
    props: {
      widthFactor: 0.7,
    },
  },
  {
    name: "sumTotalQuantity",
    label: "Item Qty",
    type: "text",
    autoFilled: true,
    allowNull: true,
    props: {
      widthFactor: 0.7,
    },
  },
  {
    name: "id",
    label: "Row Id",
    type: "text",
    allowNull: true,
    autoFilled: true,
    props: {
      widthFactor: 0.7,
    },
  },
];

export const storeTransferFields: Field[] = [
  {
    name: "sectionA",
    type: "formSection",
    alignment: "row",
    props: {
      width: "25vw",
      spacing: "2vw",
    },
    childFields: [
      {
        name: "section1",
        type: "formSection",
        alignment: "column",
        props: {
          width: "25vw",
          spacing: "0.75vw",
          justify: "top",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "docCompanyName",
                label: "Sender Company Name",
                type: "lookup",
                lookupType: "company",
                lookupCategory: "docCompany",
                allowNull: false,
                props: {
                  width: "17vw",
                },
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "docCompanyCode",
                  },
                ],
                linkedFields: [
                  {
                    name: "docLocatorCode",
                    value: "",
                  },
                  {
                    name: "docLocatorName",
                    value: "",
                  },
                ],
                disability: {
                  type: "EXISTS",
                  field: "details",
                },
              },
              {
                name: "docCompanyCode",
                label: "Company",
                type: "text",
                autoFilled: true,
                defaultValue: null,
                props: {
                  width: "7vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "docLocatorCode",
                label: "Locator Code",
                type: "text",
                props: {
                  width: "7vw",
                },
                autoFilled: true,
              },
              {
                name: "docLocatorName",
                label: "Sender Locator Name",
                props: {
                  width: "17vw",
                },
                type: "lookup",
                lookupType: "locator",
                lookupCategory: "docLocator",
                lookupParams: [
                  {
                    key: "projectCode",
                    resolveValue: true,
                    valueFieldName: "docProjectCode",
                  },
                  {
                    key: "docCompanyCode",
                    resolveValue: true,
                    valueFieldName: "docCompanyCode",
                  },
                ],
                lookupFilter: {
                  type: "AND",
                  filters: [
                    {
                      type: "IN",
                      field: "locatorType",
                      values: ["PS", "CS"],
                    },
                    {
                      type: "EQUALS",
                      field: "locatorCode",
                      resolveValue: true,
                      valueFieldName: "docLocatorCode",
                    },
                    {
                      type: "EQUALS",
                      field: "companyCode",
                      resolveValue: true,
                      valueFieldName: "docCompanyCode",
                    },
                  ],
                },
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "docLocatorCode",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "locatorType",
                    fieldName: "docLocatorType",
                  },
                  // {
                  //   additionalFieldName: "company",
                  //   lookupFieldName: "companyCode",
                  //   fieldName: "docCompanyCode",
                  // },
                  // {
                  //   additionalFieldName: "company",
                  //   lookupFieldName: "companyName",
                  //   fieldName: "docCompanyName",
                  // },
                  {
                    lookupFieldName: "description",
                    fieldName: "docStationCode",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stationName",
                    fieldName: "docStationName",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stateName",
                    fieldName: "docStateName",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "address1",
                    fieldName: "docAddress1",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "address2",
                    fieldName: "docAddress2",
                  },
                  // {
                  //   additionalFieldName: "details",
                  //   lookupFieldName: "gstNo",
                  //   fieldName: "docGSTNo",
                  // },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "todayDate",
                    fieldName: "docDate",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stateCode",
                    fieldName: "docStateCode",
                  },
                ],
                linkedFields: [
                  {
                    name: "docProjectCode",
                    value: "",
                  },
                  {
                    name: "docProjectName",
                    value: "",
                  },
                  {
                    name: "docVirtualLocator",
                    value: "",
                  },
                  {
                    name: "docOwnerCode",
                    value: "",
                  },
                  {
                    name: "docOwnerName",
                    value: "",
                  },
                  {
                    name: "docProjectType",
                    value: "",
                  },
                  {
                    name: "projectTypeLabel",
                    value: "",
                  },
                  {
                    name: "docMaterialType",
                    value: "F",
                  },
                  {
                    name: "transactionType",
                    value: "",
                  },
                  {
                    name: "senderDocNo",
                    value: null,
                  },
                  {
                    name: "senderDocDate",
                    value: null,
                  },
                  {
                    name: "receiverProjectCode",
                    value: "",
                  },
                  {
                    name: "receiverProjectName",
                    value: "",
                  },
                  {
                    name: "receiverProjectType",
                    value: "",
                  },
                  {
                    name: "receiverOwnerCode",
                    value: "",
                  },
                  {
                    name: "receiverMaterialType",
                    value: "F",
                  },
                  {
                    name: "receiverLocatorName",
                    value: "",
                  },
                  {
                    name: "receiverLocatorCode",
                    value: "",
                  },
                  {
                    name: "receiverCompanyName",
                    value: "",
                  },
                  {
                    name: "receiverCompanyCode",
                    value: "",
                  },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docCompanyCode",
                    },
                  ],
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "docStationName",
                label: "Sender Station",
                type: "text",
                defaultValue: "",
                autoFilled: true,
                props: {
                  width: "12vw",
                },
              },
              {
                name: "docStateName",
                label: "Sender State",
                type: "text",
                defaultValue: "",
                autoFilled: true,
                props: {
                  width: "12vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "projectTypeLabel",
                label: "Sender Project Type",
                type: "lookup",
                lookupType: "project",
                lookupCategory: "outwardProjectType",
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                lookupParams: [
                  {
                    key: "docLocatorCode",
                    resolveValue: true,
                    valueFieldName: "docLocatorCode",
                  },
                ],
                dependentFields: [
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "projectType",
                    fieldName: "docProjectType",
                  },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docLocatorCode",
                    },
                  ],
                },
                linkedFields: [
                  {
                    name: "docProjectCode",
                    value: "",
                  },
                  {
                    name: "docProjectName",
                    value: "",
                  },
                  {
                    name: "docVirtualLocator",
                    value: "",
                  },
                  {
                    name: "docOwnerName",
                    value: "",
                  },
                  {
                    name: "docOwnerCode",
                    value: "",
                  },
                  {
                    name: "docMaterialType",
                    value: "F",
                  },
                  {
                    name: "transactionType",
                    value: "",
                  },
                  {
                    name: "docGSTNo",
                    value: "",
                  },
                  {
                    name: "receiverProjectName",
                    value: "",
                  },
                  {
                    name: "receiverProjectCode",
                    value: "",
                  },
                  {
                    name: "receiverOwnerName",
                    value: "",
                  },
                  {
                    name: "receiverOwnerCode",
                    value: "",
                  },
                  {
                    name: "receiverGSTNo",
                    value: "",
                  },
                ],
              },
              {
                type: "text",
                label: "Project Type Dummy",
                name: "docProjectType",
                visibility: {
                  type: "IN",
                  field: "docProjectType",
                  values: ["dummy"],
                },
                linkedFields: [
                  {
                    name: "receiverProjectType",
                    resolveValue: true,
                    valueFieldName: "docProjectType",
                  },
                ],
              },
              {
                name: "docMaterialType",
                label: "Sender Material Type",
                type: "picklist",
                allowUpdate: true,
                defaultValue: "F",
                values: materialTypeValues,
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docLocatorCode",
                    },
                  ],
                },
                props: {
                  width: "12vw",
                },
                linkedFields: [
                  {
                    name: "receiverMaterialType",
                    filteredValues: [
                      {
                        filter: { type: "NIN", field: "transactionType", values: ["IS"] },
                        resolveValue: true,
                        valueFieldName: "docMaterialType",
                      },
                    ],
                    value: "",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "docOwnerName",
                label: "Proj. Owner",
                type: "lookup",
                lookupType: "company",
                lookupCategory: "outwardProjectOwner",
                defaultValue: null,
                props: {
                  width: "17vw",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docCompanyCode",
                    },
                    {
                      type: "MISSING",
                      field: "docProjectType",
                    },
                  ],
                },

                lookupParams: [
                  {
                    key: "docLocatorCode",
                    resolveValue: true,
                    valueFieldName: "docLocatorCode",
                  },
                  {
                    key: "projectType",
                    resolveValue: true,
                    valueFieldName: "docProjectType",
                  },
                ],
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "docOwnerCode",
                  },
                ],
                linkedFields: [
                  {
                    name: "docProjectCode",
                    value: "",
                  },
                  {
                    name: "docProjectName",
                    value: "",
                  },
                  {
                    name: "docVirtualLocator",
                    value: "",
                  },
                  {
                    name: "transactionType",
                    value: "",
                  },
                  {
                    name: "docGSTNo",
                    value: "",
                  },
                  {
                    name: "receiverProjectName",
                    value: "",
                  },
                  {
                    name: "receiverProjectCode",
                    value: "",
                  },
                  {
                    name: "receiverGSTNo",
                    value: "",
                  },
                  {
                    name: "receiverOwnerName",
                    resolveValue: true,
                    valueFieldName: "docOwnerName",
                  },
                ],
              },
              {
                name: "docOwnerCode",
                label: "Proj. Owner",
                type: "text",
                autoFilled: true,
                allowNull: true,
                defaultValue: null,
                props: {
                  width: "7vw",
                },
                linkedFields: [
                  {
                    name: "receiverOwnerCode",
                    resolveValue: true,
                    valueFieldName: "docOwnerCode",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "senderDocType",
                label: "Sender Doc Type",
                type: "picklist",
                allowUpdate: true,
                defaultValue: "NA",
                props: {
                  width: "12vw",
                },
                values: senderDocTypeValues,
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "MISSING",
                      field: "docOwnerCode",
                    },
                    {
                      type: "MISSING",
                      field: "docLocatorCode",
                    },
                    {
                      type: "AND",
                      filters: [
                        {
                          type: "IN",
                          field: "docProjectType",
                          values: ["FTTH"],
                        },
                        {
                          type: "IN",
                          field: "docLocatorType",
                          values: ["CS"],
                        },
                      ],
                    },
                  ],
                },
                linkedFields: [
                  { name: "senderDocNo", value: null },
                  { name: "senderDocDate", value: null },
                ],
              },
              {
                name: "docLocatorType",
                label: "Locator Type",
                type: "picklist",
                props: {
                  width: "12vw",
                },
                values: locatorTypeValues,
                autoFilled: true,
                allowNull: true,
                defaultValue: null,
              },
              {
                name: "docGSTNo",
                label: "Sender GST No",
                type: "text",
                allowNull: true,
                autoFilled: true,
                displayOnly: true,
                props: {
                  width: "12vw",
                },
                visibility: {
                  type: "IN",
                  field: "docProjectType",
                  values: ["dummy"],
                },
                linkedFields: [
                  {
                    name: "senderGSTNo",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "transactionType", values: ["SL", "PS"] },
                        resolveValue: true,
                        valueFieldName: "docGSTNo",
                      },
                    ],
                    value: "",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "senderDocNo",
                label: "Sender Doc No",
                type: "lookup",
                lookupType: "child-work-order",
                lookupCategory: "inwardChildWorkOrder",
                lookupFilter: {
                  type: "AND",
                  filters: [
                    {
                      type: "EQUALS",
                      field: "companyCode",
                      resolveValue: true,
                      valueFieldName: "docCompanyCode",
                    },
                    {
                      type: "EQUALS",
                      field: "projectCode",
                      resolveValue: true,
                      valueFieldName: "docProjectCode",
                    },
                  ],
                },
                allowUpdate: true,
                defaultValue: "",
                dependentFields: [
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "cwoDate",
                    fieldName: "senderDocDate",
                  },
                ],
                props: {
                  width: "12vw",
                },
                visibility: {
                  type: "IN",
                  field: "senderDocType",
                  values: ["CO"],
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "NIN",
                      field: "senderDocType",
                      values: ["CO"],
                    },
                    {
                      type: "MISSING",
                      field: "docCompanyCode",
                    },
                  ],
                },
              },
              {
                name: "senderDocNo",
                label: "Sender Doc No",
                type: "text",
                defaultValue: "",
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                disability: {
                  type: "IN",
                  field: "senderDocType",
                  values: ["NA", "CO"],
                },
                visibility: {
                  type: "NIN",
                  field: "senderDocType",
                  values: ["CO"],
                },
              },
              {
                name: "senderDocDate",
                label: "Sender Doc Date",
                type: "date",
                constraints: [{ type: "DATE_RANGE", beforeNow: true, maxDateScript: { operation: "maxDate", valueFields: ["docDate"] } }],
                allowUpdate: true,
                defaultValue: null,
                props: {
                  width: "12vw",
                },
                disability: {
                  type: "IN",
                  field: "senderDocType",
                  values: ["NA", "CO"],
                },
              },
            ],
          },
        ],
      },
      {
        name: "section2",
        type: "formSection",
        alignment: "column",
        props: {
          width: "21vw",
          spacing: "0.75vw",
          justify: "top",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            props: {
              justify: "right",
            },
            childFields: [
              {
                name: "sections",
                type: "formSection",
                childFields: [
                  {
                    name: "transactionType",
                    label: "Transaction Type",
                    type: "picklist",
                    defaultValue: "",
                    allowUpdate: true,
                    values: transferTransactionTypeValues,
                    linkedFields: [
                      {
                        name: "withinState",
                        value: true,
                      },
                      {
                        name: "receiverCompanyName",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docCompanyName",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverCompanyCode",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docCompanyCode",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverLocatorName",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docLocatorName",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverLocatorCode",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docLocatorCode",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverLocatorType",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docLocatorType",
                          },
                        ],
                        value: "",
                      },
                      // {
                      //   name: "receiverMaterialType",
                      //   filteredValues: [
                      //     {
                      //       filter: { type: "NIN", field: "transactionType", values: ["IS"] },
                      //       resolveValue: true,
                      //       valueFieldName: "docMaterialType",
                      //     },
                      //   ],
                      //   value: "",
                      // },
                      {
                        name: "receiverProjectName",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                            resolveValue: true,
                            valueFieldName: "docProjectName",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverProjectCode",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                            resolveValue: true,
                            valueFieldName: "docProjectCode",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverProjectType",
                        resolveValue: true,
                        valueFieldName: "docProjectType",
                      },
                      {
                        name: "receiverOwnerName",
                        resolveValue: true,
                        valueFieldName: "docOwnerName",
                      },
                      {
                        name: "receiverOwnerCode",
                        resolveValue: true,
                        valueFieldName: "docOwnerCode",
                      },
                      {
                        name: "receiverGSTNo",
                        resolveValue: true,
                        valueFieldName: "docGSTNo",
                      },
                      {
                        name: "receiverAddress1",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docAddress1",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverAddress2",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docAddress2",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverStationName",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docStationName",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverStateName",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docStateName",
                          },
                        ],
                        value: "",
                      },
                      {
                        name: "receiverStationCode",
                        filteredValues: [
                          {
                            filter: { type: "IN", field: "transactionType", values: ["IS"] },
                            resolveValue: true,
                            valueFieldName: "docStationCode",
                          },
                        ],
                        value: "",
                      },
                      // {
                      //   name: "autoInward",
                      //   filteredValues: [
                      //     {
                      //       filter: {
                      //         type: "AND",
                      //         filters: [
                      //           { type: "IN", field: "autoGP", values: [true] },
                      //           { type: "IN", field: "transactionType", values: ["IS", "PS", "CS"] },
                      //         ],
                      //       },
                      //       value: true,
                      //     },
                      //   ],
                      //   value: false,
                      // },
                      {
                        name: "receiverDocNo",
                        value: "",
                      },
                      {
                        name: "receiverDocDate",
                        value: "",
                      },
                    ],
                    props: {
                      width: "13vw",
                    },
                    disability: {
                      type: "OR",
                      filters: [
                        {
                          type: "EXISTS",
                          field: "details",
                        },
                        {
                          type: "MISSING",
                          field: "docLocatorCode",
                        },
                        {
                          type: "MISSING",
                          field: "docOwnerCode",
                        },
                      ],
                    },
                  },
                ],
              },
              {
                name: "docProjectCode",
                label: "Proj. Code",
                type: "text",
                autoFilled: true,
                allowNull: true,
                props: {
                  width: "7vw",
                },
                linkedFields: [
                  {
                    name: "receiverProjectCode",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docProjectCode",
                      },
                    ],
                    value: "",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "projectShiftFlg",
                label: "Project Shift",
                type: "checkbox",
                allowUpdate: true,
                defaultValue: false,
                props: {
                  width: "10vw",
                  justify: "right",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "transactionType",
                    },
                    {
                      type: "IN",
                      field: "multiProjectFlg",
                      values: [true],
                    },
                  ],
                },
                linkedFields: [
                  {
                    name: "withinState",
                    value: true,
                  },
                  {
                    name: "receiverProjectName",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docProjectName",
                      },
                    ],
                    value: "",
                  },
                  {
                    name: "receiverProjectCode",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docProjectCode",
                      },
                    ],
                    value: "",
                  },
                  {
                    name: "receiverVirtualLocator",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docVirtualLocator",
                      },
                    ],
                    value: "",
                  },
                ],
              },
              {
                name: "multiProjectFlg",
                label: "Multi Project",
                type: "checkbox",
                allowUpdate: true,
                defaultValue: false,
                props: {
                  width: "10vw",
                  justify: "right",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "transactionType",
                    },
                    {
                      type: "IN",
                      field: "projectShiftFlg",
                      values: [true],
                    },
                  ],
                },
                linkedFields: [
                  {
                    name: "withinState",
                    value: true,
                  },
                  {
                    name: "docProjectName",
                    value: "",
                  },
                  {
                    name: "docProjectCode",
                    value: "",
                  },
                  {
                    name: "docVirtualLocator",
                    value: "",
                  },
                  {
                    name: "receiverProjectName",
                    value: "",
                  },
                  {
                    name: "receiverProjectCode",
                    value: "",
                  },
                  {
                    name: "receiverVirtualLocator",
                    value: "",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            visibility: {
              type: "NIN",
              field: "multiProjectFlg",
              values: [true],
            },
            childFields: [
              {
                name: "docProjectName",
                label: "Sender Project Name",
                type: "lookup",
                allowUpdate: true,
                lookupType: "project",
                lookupCategory: "outwardProject",
                props: {
                  width: "21vw",
                },
                lookupParams: [
                  {
                    key: "docLocatorCode",
                    resolveValue: true,
                    valueFieldName: "docLocatorCode",
                  },
                  {
                    key: "projectType",
                    resolveValue: true,
                    valueFieldName: "docProjectType",
                  },
                  {
                    key: "docOwnerCode",
                    resolveValue: true,
                    valueFieldName: "docOwnerCode",
                  },
                ],
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "docProjectCode",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "virtualLocator",
                    fieldName: "docVirtualLocator",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "closed",
                    fieldName: "projectClosed",
                  },
                  // {
                  //   additionalFieldName: "details",
                  //   lookupFieldName: "ownerCode",
                  //   fieldName: "docOwnerCode",
                  // },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "gstNo",
                    fieldName: "docGSTNo",
                  },
                ],
                warningMessageConfig: {
                  filter: { type: "IN", field: "projectClosed", values: [true] },
                  message: "Project is closed",
                },
                linkedFields: [
                  // {
                  //   name: "receiverProjectType",
                  //   resolveValue: true,
                  //   valueFieldName: "docProjectType",
                  // },
                  {
                    name: "receiverProjectName",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docProjectName",
                      },
                    ],
                    value: "",
                  },
                  // {
                  //   name: "receiverLocatorName",
                  //   value: "",
                  // },
                  // {
                  //   name: "receiverLocatorCode",
                  //   value: "",
                  // },
                  // {
                  //   name: "receiverCompanyName",
                  //   value: "",
                  // },
                  // {
                  //   name: "receiverCompanyCode",
                  //   value: "",
                  // },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docProjectType",
                    },
                    {
                      type: "MISSING",
                      field: "docLocatorCode",
                    },
                    {
                      type: "MISSING",
                      field: "docOwnerCode",
                    },
                    {
                      type: "IN",
                      field: "multiProjectFlg",
                      values: [true],
                    },
                  ],
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            visibility: {
              type: "IN",
              field: "transactionType",
              values: ["dummy"],
            },
            childFields: [
              {
                name: "docVirtualLocator",
                label: "Sender Virtual Locator",
                type: "text",
                allowNull: true,
                autoFilled: true,
                displayOnly: true,
                props: {
                  width: "21vw",
                },
                linkedFields: [
                  {
                    name: "receiverVirtualLocator",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "projectShiftFlg", values: [false] },
                        resolveValue: true,
                        valueFieldName: "docVirtualLocator",
                      },
                    ],
                    value: "",
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverVirtualLocator",
                label: "Receiver Virtual Locator",
                type: "text",
                allowNull: true,
                autoFilled: true,
                displayOnly: true,
                props: {
                  width: "21vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "id",
                label: "MOA No",
                type: "text",
                displayOnly: true,
                allowNull: true,
                props: {
                  width: "10vw",
                },
              },
              {
                name: "docDate",
                label: "MOA Date",
                type: "date",
                allowUpdate: true,
                strictDate: true,
                constraints: [
                  {
                    type: "DATE_RANGE",
                    minDateScript: {
                      operation: "maxDate",
                      valueFields: ["senderDocDate", "receiverDocDate"],
                      globalFields: [WORK_START_DATE_FIELD, OUTWARD_CREATION_MIN_DATE],
                    },
                    beforeNow: true,
                  },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docLocatorCode",
                    },
                  ],
                },
                props: {
                  width: "10vw",
                },
              },
            ],
          },

          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "sections",
                type: "formSection",
                childFields: [
                  {
                    name: "withinState",
                    label: "Within State",
                    type: "checkbox",
                    defaultValue: true,
                    allowUpdate: true,
                    linkedFields: [
                      {
                        name: "receiverLocatorName",
                        value: "",
                      },
                      {
                        name: "receiverLocatorCode",
                        value: "",
                      },
                      {
                        name: "receiverCompanyName",
                        value: "",
                      },
                      {
                        name: "receiverCompanyCode",
                        value: "",
                      },
                    ],
                    disability: {
                      type: "OR",
                      filters: [
                        {
                          type: "EXISTS",
                          field: "details",
                        },
                        {
                          type: "MISSING",
                          field: "transactionType",
                        },
                        {
                          type: "IN",
                          field: "docLocatorType",
                          values: ["CS", "OT", "ST"],
                        },
                        {
                          type: "IN",
                          field: "transactionType",
                          values: ["CS", "IS"],
                        },
                        {
                          type: "AND",
                          filters: [
                            {
                              type: "IN",
                              field: "projectShiftFlg",
                              values: [false],
                            },
                            {
                              type: "IN",
                              field: "multiProjectFlg",
                              values: [false],
                            },
                          ],
                        },
                        // {
                        //   type: "MISSING",
                        //   field: "docOwnerCode",
                        // },
                        // {
                        //   type: "AND",
                        //   filters: [
                        //     {
                        //       type: "IN",
                        //       field: "multiProjectFlg",
                        //       values: [false],
                        //     },
                        //     {
                        //       type: "MISSING",
                        //       field: "docProjectCode",
                        //     },
                        //   ],
                        // },
                        // {
                        //   type: "MISSING",
                        //   field: "transactionType",
                        // },
                        // {
                        //   type: "IN",
                        //   field: "transactionType",
                        //   values: ["IS"],
                        // },
                      ],
                    },
                  },
                ],
              },
              {
                name: "sections",
                type: "formSection",
                childFields: [
                  {
                    name: "gpFlg",
                    label: "Is GP Made",
                    type: "checkbox",
                    props: {
                      width: "10vw",
                      justify: "center",
                    },
                    defaultValue: false,
                    displayOnly: true,
                  },
                ],
              },
            ],
          },

          {
            name: "sections",
            type: "formSection",
            visibility: {
              type: "IN",
              field: "gpFlg",
              values: [true],
            },
            childFields: [
              {
                name: "gpId",
                label: "Gate Pass Id",
                type: "number",
                allowNull: true,
                autoFilled: true,
                props: {
                  width: "10vw",
                },
              },
              {
                name: "gpDate",
                label: "Gate Pass Date",
                type: "date",
                allowNull: true,
                autoFilled: true,
                props: {
                  width: "10vw",
                },
              },
            ],
          },
        ],
      },
      {
        name: "section3",
        type: "formSection",
        alignment: "column",
        props: {
          width: "25vw",
          spacing: "0.75vw",
          justify: "top",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverCompanyName",
                label: "Receiver Company Name",
                type: "lookup",
                lookupType: "company",
                allowUpdate: true,
                lookupCategory: "receiverCompany",
                props: {
                  width: "17vw",
                },
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "receiverCompanyCode",
                  },
                ],
                lookupParams: [
                  {
                    key: "docLocatorCode",
                    resolveValue: true,
                    valueFieldName: "docLocatorCode",
                  },
                  {
                    key: "projectOwnerCode",
                    resolveValue: true,
                    valueFieldName: "docOwnerCode",
                  },
                  {
                    key: "docCompanyCode",
                    resolveValue: true,
                    valueFieldName: "docCompanyCode",
                  },
                  {
                    key: "transactionType",
                    resolveValue: true,
                    valueFieldName: "transactionType",
                  },
                  {
                    key: "receiverProjectCode",
                    resolveValue: true,
                    valueFieldName: "receiverProjectCode",
                  },
                  {
                    key: "receiverLocatorCode",
                    resolveValue: true,
                    valueFieldName: "receiverLocatorCode",
                  },
                  {
                    key: "withinState",
                    resolveValue: true,
                    valueFieldName: "withinState",
                  },
                  {
                    key: "docStateCode",
                    resolveValue: true,
                    valueFieldName: "docStateCode",
                  },
                ],
                linkedFields: [
                  {
                    name: "receiverLocatorName",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "transactionType", values: ["IS"] },
                        resolveValue: true,
                        valueFieldName: "docLocatorName",
                      },
                    ],
                    value: "",
                  },
                  {
                    name: "receiverLocatorCode",
                    filteredValues: [
                      {
                        filter: { type: "IN", field: "transactionType", values: ["IS"] },
                        resolveValue: true,
                        valueFieldName: "docLocatorCode",
                      },
                    ],
                    value: "",
                  },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "docCompanyCode",
                    },
                    {
                      type: "MISSING",
                      field: "docOwnerCode",
                    },
                    {
                      type: "AND",
                      filters: [
                        {
                          type: "IN",
                          field: "multiProjectFlg",
                          values: [false],
                        },
                        {
                          type: "MISSING",
                          field: "docProjectCode",
                        },
                      ],
                    },
                    {
                      type: "MISSING",
                      field: "transactionType",
                    },
                    {
                      type: "IN",
                      field: "transactionType",
                      values: ["IS"],
                    },
                  ],
                },
              },
              {
                name: "receiverCompanyCode",
                label: "Code",
                type: "text",
                autoFilled: true,
                allowNull: true,
                props: {
                  width: "7vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverLocatorCode",
                label: "Code",
                type: "text",
                autoFilled: true,
                allowNull: true,
                props: {
                  width: "7vw",
                },
              },
              {
                name: "receiverLocatorName",
                label: "Receiver Locator Name",
                type: "lookup",
                lookupType: "locator",
                lookupCategory: "receiverLocator",
                allowUpdate: true,
                props: {
                  width: "17vw",
                },
                lookupParams: [
                  {
                    key: "withinState",
                    resolveValue: true,
                    valueFieldName: "withinState",
                  },
                  {
                    key: "docStateCode",
                    resolveValue: true,
                    valueFieldName: "docStateCode",
                  },
                  {
                    key: "docCompanyCode",
                    resolveValue: true,
                    valueFieldName: "docCompanyCode",
                  },
                  {
                    key: "docLocatorCode",
                    resolveValue: true,
                    valueFieldName: "docLocatorCode",
                  },
                  {
                    key: "transactionType",
                    resolveValue: true,
                    valueFieldName: "transactionType",
                  },
                  {
                    key: "receiverProjectCode",
                    resolveValue: true,
                    valueFieldName: "receiverProjectCode",
                  },
                  {
                    key: "receiverProjectType",
                    resolveValue: true,
                    valueFieldName: "receiverProjectType",
                  },
                  {
                    key: "locatorCompany",
                    resolveValue: true,
                    valueFieldName: "receiverCompanyCode",
                  },
                ],
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "receiverLocatorCode",
                  },
                  {
                    lookupFieldName: "description",
                    fieldName: "receiverStationCode",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stationName",
                    fieldName: "receiverStationName",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stateCode",
                    fieldName: "receiverStateCode",
                  },
                  {
                    additionalFieldName: "station",
                    lookupFieldName: "stateName",
                    fieldName: "receiverStateName",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "gstNo",
                    fieldName: "receiverGSTNo",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "address1",
                    fieldName: "receiverAddress1",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "address2",
                    fieldName: "receiverAddress2",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "locatorType",
                    fieldName: "receiverLocatorType",
                  },
                ],
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "EXISTS",
                      field: "details",
                    },
                    {
                      type: "MISSING",
                      field: "receiverCompanyCode",
                    },
                    {
                      type: "MISSING",
                      field: "transactionType",
                    },
                    {
                      type: "IN",
                      field: "transactionType",
                      values: ["IS"],
                    },
                  ],
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverStationName",
                label: "Receiver Station",
                type: "text",
                autoFilled: true,
                props: {
                  width: "12vw",
                },
              },
              {
                name: "receiverStateName",
                label: "Receiver State/Country",
                type: "text",
                autoFilled: true,
                props: {
                  width: "12vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverProjectType",
                label: "Receiver Project Type",
                type: "picklist",
                values: outProjectTypeValues,
                autoFilled: true,
                props: {
                  width: "12vw",
                },
                displayOnly: true,
              },
              // {
              //   name: "receiverLocatorType",
              //   label: "Locator Type",
              //   type: "picklist",
              //   props: {
              //     width: "12vw",
              //   },
              //   values: locatorTypeValues,
              //   autoFilled: true,
              //   allowNull: true,
              //   defaultValue: null,
              // },
              {
                name: "receiverMaterialType",
                label: "Receiver Material Type",
                type: "picklist",
                values: materialTypeValues,
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "NIN",
                      field: "transactionType",
                      values: ["IS"],
                    },
                    {
                      type: "MISSING",
                      field: "receiverLocatorCode",
                    },
                  ],
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            visibility: {
              type: "IN",
              field: "transactionType",
              values: ["dummy"],
            },
            childFields: [
              {
                name: "receiverOwnerCode",
                label: "Proj. Owner",
                type: "text",
                autoFilled: true,
                displayOnly: true,
                allowNull: true,
                props: {
                  width: "7vw",
                },
              },
              {
                name: "receiverOwnerName",
                label: "Proj. Owner",
                type: "text",
                autoFilled: true,
                displayOnly: true,
                defaultValue: null,
                props: {
                  width: "17vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverProjectName",
                label: "Receiver Project Name",
                type: "lookup",
                allowUpdate: true,
                lookupType: "project",
                lookupCategory: "receiverProject",
                lookupParams: [
                  {
                    key: "receiverStateCode",
                    resolveValue: true,
                    valueFieldName: "receiverStateCode",
                  },
                  {
                    key: "docProjectCode",
                    resolveValue: true,
                    valueFieldName: "docProjectCode",
                  },
                  {
                    key: "projectShiftFlg",
                    resolveValue: true,
                    valueFieldName: "projectShiftFlg",
                  },
                  {
                    key: "locatorCode",
                    resolveValue: true,
                    valueFieldName: "receiverLocatorCode",
                  },
                  {
                    key: "projectType",
                    resolveValue: true,
                    valueFieldName: "receiverProjectType",
                  },
                  {
                    key: "receiverLocatorType",
                    resolveValue: true,
                    valueFieldName: "receiverLocatorType",
                  },
                  {
                    key: "projectOwnerCode",
                    resolveValue: true,
                    valueFieldName: "docOwnerCode",
                  },
                  {
                    key: "stateCode",
                    resolveValue: true,
                    valueFieldName: "receiverStateCode",
                  },
                ],
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "receiverProjectCode",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "virtualLocator",
                    fieldName: "receiverVirtualLocator",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "ownerCode",
                    fieldName: "receiverOwnerCode",
                  },
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "gstNo",
                    fieldName: "receiverGSTNo",
                  },
                ],
                props: {
                  width: "18vw",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "MISSING",
                      field: "receiverLocatorCode",
                    },
                    {
                      type: "AND",
                      filters: [
                        {
                          type: "IN",
                          field: "multiProjectFlg",
                          values: [false],
                        },
                        {
                          type: "IN",
                          field: "projectShiftFlg",
                          values: [false],
                        },
                      ],
                    },
                  ],
                },
              },
              {
                name: "receiverProjectCode",
                label: "Proj. Code",
                type: "text",
                autoFilled: true,
                defaultValue: "",
                props: {
                  width: "6vw",
                },
                displayOnly: true,
              },
            ],
          },
          // {
          //   name: "sections",
          //   type: "formSection",
          //   visibility: {
          //     type: "NIN",
          //     field: "transactionType",
          //     values: ["IS", "CS", "PS"],
          //   },
          //   childFields: [
          //     {
          //       name: "receiverAddress1",
          //       label: "Address1 ",
          //       type: "text",
          //       autoFilled: true,
          //       props: {
          //         width: "25vw",
          //       },
          //     },
          //   ],
          // },
          // {
          //   name: "sections",
          //   type: "formSection",
          //   visibility: {
          //     type: "NIN",
          //     field: "transactionType",
          //     values: ["IS", "CS", "PS"],
          //   },
          //   childFields: [
          //     {
          //       name: "receiverAddress2",
          //       label: "Address2 ",
          //       type: "text",
          //       allowNull: true,
          //       autoFilled: true,
          //       props: {
          //         width: "25vw",
          //       },
          //     },
          //   ],
          // },
          // {
          //   name: "sections",
          //   type: "formSection",
          //   visibility: {
          //     type: "NIN",
          //     field: "transactionType",
          //     values: ["IS", "CS", "PS"],
          //   },
          //   childFields: [
          //     {
          //       name: "receiverStationName",
          //       label: "Receiver Station",
          //       type: "text",
          //       autoFilled: true,
          //       props: {
          //         width: "12vw",
          //       },
          //     },
          //     {
          //       name: "receiverStateName",
          //       label: "Receiver State/Country",
          //       type: "text",
          //       autoFilled: true,
          //       props: {
          //         width: "12vw",
          //       },
          //     },
          //   ],
          // },
          // {
          //   name: "sections",
          //   type: "formSection",
          //   visibility: {
          //     type: "NIN",
          //     field: "transactionType",
          //     values: ["IS", "CS", "PS"],
          //   },
          //   childFields: [
          //     {
          //       name: "receiverLocatorType",
          //       label: "Locator Type",
          //       type: "picklist",
          //       props: {
          //         width: "12vw",
          //       },
          //       values: locatorTypeValues,
          //       autoFilled: true,
          //       allowNull: true,
          //       defaultValue: null,
          //     },
          //     {
          //       name: "receiverMaterialType",
          //       label: "Receiver Material Type",
          //       type: "picklist",
          //       values: materialTypeValues,
          //       allowUpdate: true,
          //       props: {
          //         width: "12vw",
          //       },
          //       disability: {
          //         type: "OR",
          //         filters: [
          //           {
          //             type: "NIN",
          //             field: "transactionType",
          //             values: ["IS"],
          //           },
          //           {
          //             type: "MISSING",
          //             field: "receiverLocatorCode",
          //           },
          //         ],
          //       },
          //     },
          //   ],
          // },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverDocType",
                label: "Receiver Doc Type",
                type: "picklist",
                defaultValue: "NA",
                allowUpdate: true,
                allowNull: false,
                props: {
                  width: "12vw",
                },
                values: receiverDocTypeValues,
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "MISSING",
                      field: "receiverLocatorCode",
                    },
                    {
                      type: "AND",
                      filters: [
                        {
                          type: "IN",
                          field: "transactionType",
                          values: ["CS"],
                        },
                        {
                          type: "IN",
                          field: "docProjectType",
                          values: ["FTTH"],
                        },
                      ],
                    },
                  ],
                },
                linkedFields: [
                  { name: "receiverDocNo", value: null },
                  { name: "receiverDocDate", value: null },
                ],
              },
              {
                name: "receiverGSTNo",
                label: "Receiver GST No",
                type: "text",
                allowNull: true,
                autoFilled: true,
                displayOnly: true,
                props: {
                  width: "12vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "receiverDocNo",
                label: "Receiver Doc No",
                type: "text",
                allowUpdate: true,
                defaultValue: "",
                props: {
                  width: "12vw",
                },
                disability: {
                  type: "NIN",
                  field: "receiverDocType",
                  values: ["PO", "LO", "MR"],
                },
                visibility: {
                  type: "NIN",
                  field: "receiverDocType",
                  values: ["CO"],
                },
              },
              {
                name: "receiverDocNo",
                label: "Receiver Doc No",
                type: "lookup",
                lookupType: "child-work-order",
                lookupCategory: "inwardChildWorkOrder",
                lookupFilter: {
                  type: "AND",
                  filters: [
                    {
                      type: "EQUALS",
                      field: "companyCode",
                      resolveValue: true,
                      valueFieldName: "receiverCompanyCode",
                    },
                    {
                      type: "EQUALS",
                      field: "projectCode",
                      resolveValue: true,
                      valueFieldName: "receiverProjectCode",
                    },
                  ],
                },
                allowUpdate: true,
                defaultValue: "",
                dependentFields: [
                  {
                    additionalFieldName: "details",
                    lookupFieldName: "cwoDate",
                    fieldName: "receiverDocDate",
                  },
                ],
                props: {
                  width: "12vw",
                },
                visibility: {
                  type: "IN",
                  field: "receiverDocType",
                  values: ["CO"],
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "NIN",
                      field: "receiverDocType",
                      values: ["CO"],
                    },
                    {
                      type: "MISSING",
                      field: "receiverLocatorCode",
                    },
                  ],
                },
              },
              {
                name: "receiverDocDate",
                label: "Receiver Doc Date",
                type: "date",
                constraints: [{ type: "DATE_RANGE", beforeNow: true, maxDateScript: { operation: "maxDate", valueFields: ["docDate"] } }],
                allowUpdate: true,
                defaultValue: null,
                props: {
                  width: "12vw",
                },
                disability: {
                  type: "IN",
                  field: "receiverDocType",
                  values: ["CO", "NA"],
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: "details",
    label: "Details",
    allowUpdate: true,
    allowNull: true,
    type: "formTable",
    tableFields: storeTransferDetailFields,
    disability: {
      type: "OR",
      filters: [
        {
          type: "MISSING",
          field: "receiverLocatorCode",
        },
        {
          type: "MISSING",
          field: "docMaterialType",
        },
        {
          type: "MISSING",
          field: "transactionType",
        },
      ],
    },
    props: {
      width: "80vw",
    },
  },
  {
    name: "sectionB",
    type: "formSection",
    alignment: "row",
    props: {
      width: "25vw",
      spacing: "2vw",
      justify: "start",
    },
    childFields: [
      {
        name: "section1",
        type: "formSection",
        alignment: "column",
        props: {
          width: "25vw",
          spacing: "0.75vw",
          justify: "top",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "transportMode",
                label: "Transport Mode",
                type: "picklist",
                defaultValue: "SF",
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                values: [
                  { value: "TP", label: "Transporter" },
                  { value: "CR", label: "Courier" },
                  { value: "SF", label: "Self" },
                ],
                disability: {
                  field: "autoGP",
                  type: "IN",
                  values: [false],
                },
              },
              {
                name: "vehicleNo",
                label: "Vehicle Number",
                type: "text",
                allowNull: true,
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                disability: {
                  field: "autoGP",
                  type: "IN",
                  values: [false],
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "vendorName",
                label: "Vendor Name",
                type: "lookup",
                lookupType: "company",
                defaultValue: null,
                allowUpdate: true,
                dependentFields: [
                  {
                    lookupFieldName: "id",
                    fieldName: "vendorCode",
                  },
                ],
                lookupFilter: {
                  type: "AND",
                  filters: [
                    {
                      type: "EQUALS",
                      field: "companyCode",
                      resolveValue: true,
                      valueFieldName: "vendorCode",
                    },
                    {
                      type: "EQUALS",
                      field: "ownerFlg",
                      values: false,
                    },
                    {
                      type: "EQUALS",
                      field: "serviceFlg",
                      values: true,
                    },
                  ],
                },
                props: {
                  width: "17vw",
                },
                disability: {
                  field: "transportMode",
                  type: "NIN",
                  values: ["TP", "CR"],
                },
              },
              {
                name: "vendorCode",
                label: "Code",
                type: "text",
                defaultValue: null,
                allowNull: true,
                autoFilled: true,
                props: {
                  width: "7vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "consignmentNote",
                label: "Consignment Note",
                type: "text",
                allowNull: true,
                defaultValue: null,
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                disability: {
                  field: "transportMode",
                  type: "NIN",
                  values: ["TP", "CR"],
                },
              },
              {
                name: "ewayBillNo",
                label: "Eway Bill No",
                type: "text",
                allowNull: true,
                allowUpdate: true,
                props: {
                  width: "12vw",
                },
                disability: {
                  field: "autoGP",
                  type: "IN",
                  values: [false],
                },
              },
            ],
          },
        ],
      },
      {
        name: "section2",
        type: "formSection",
        alignment: "column",
        props: {
          width: "25vw",
          spacing: "0.75vw",
          justify: "top",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            props: {
              width: "21vw",
              justify: "center",
            },
            childFields: [
              {
                name: "cancelled",
                label: "Cancelled",
                type: "checkbox",
                props: {
                  width: "21vw",
                  justify: "center",
                },
                defaultValue: false,
                allowUpdate: true,
                disabledForCreation: true,
                disability: {
                  type: "IN",
                  field: "submit",
                  values: [true],
                },
                linkedFields: [
                  {
                    name: "autoGP",
                    value: false,
                  },
                ],
              },
              {
                name: "submit",
                label: "Submit",
                type: "checkbox",
                props: {
                  width: "21vw",
                  justify: "center",
                },
                defaultValue: false,
                allowUpdate: true,
                disability: {
                  type: "IN",
                  field: "cancelled",
                  values: [true],
                },
                linkedFields: [
                  {
                    name: "autoGP",
                    value: false,
                  },
                  {
                    name: "autoInward",
                    value: false,
                  },
                  // {
                  //   name: "autoGP",
                  //   resolveValue: true,
                  //   valueFieldName: "submit",
                  // },
                  // {
                  //   name: "autoInward",
                  //   resolveValue: true,
                  //   valueFieldName: "submit",
                  // },
                  {
                    name: "transportMode",
                    value: "SF",
                  },
                  {
                    name: "vehicleNo",
                    value: null,
                  },
                  {
                    name: "vendorName",
                    value: null,
                  },
                  {
                    name: "vendorCode",
                    value: null,
                  },
                  {
                    name: "consignmentNote",
                    value: null,
                  },
                  {
                    name: "ewayBillNo",
                    value: null,
                  },
                  {
                    name: "totalPkgs",
                    value: 0,
                  },
                ],
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "totalPkgs",
                label: "Total Packages",
                type: "number",
                defaultValue: 0,
                allowUpdate: true,
                props: {
                  width: "10vw",
                },
                disability: {
                  field: "autoGP",
                  type: "IN",
                  values: [false],
                },
              },
              {
                name: "totalValue",
                label: "Total Value",
                type: "text",
                defaultValue: "0",
                allowNull: true,
                autoFilled: true,
                props: {
                  width: "10vw",
                },
              },
            ],
          },
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "autoGP",
                label: "Auto GP",
                type: "checkbox",
                defaultValue: false,
                allowUpdate: true,
                props: {
                  width: "10vw",
                  justify: "left",
                },
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "IN",
                      field: "submit",
                      values: [false],
                    },
                    {
                      type: "IN",
                      field: "gpFlg",
                      values: [true],
                    },
                  ],
                },
                linkedFields: [
                  {
                    name: "transportMode",
                    value: "SF",
                  },
                  {
                    name: "vehicleNo",
                    value: null,
                  },
                  {
                    name: "vendorName",
                    value: null,
                  },
                  {
                    name: "vendorCode",
                    value: null,
                  },
                  {
                    name: "consignmentNote",
                    value: null,
                  },
                  {
                    name: "ewayBillNo",
                    value: null,
                  },
                  {
                    name: "totalPkgs",
                    value: 0,
                  },
                  {
                    name: "autoInward",
                    resolveValue: true,
                    valueFieldName: "autoGP",
                  },
                ],
              },
              {
                name: "autoInward",
                label: "Auto Inward",
                type: "checkbox",
                defaultValue: false,
                allowUpdate: true,
                disability: {
                  type: "OR",
                  filters: [
                    {
                      type: "IN",
                      field: "autoGP",
                      values: [false],
                    },
                  ],
                },
                props: {
                  width: "10vw",
                  justify: "right",
                },
              },
            ],
          },
        ],
      },
      {
        name: "section3",
        type: "formSection",
        alignment: "column",
        props: {
          width: "21vw",
          spacing: "0.75vw",
          justify: "start",
        },
        childFields: [
          {
            name: "sections",
            type: "formSection",
            childFields: [
              {
                name: "remarks",
                label: "Remarks",
                type: "text",
                allowUpdate: true,
                allowNull: true,
                props: {
                  width: "17vw",
                },
              },
              {
                name: "mirId",
                label: "MIR No",
                type: "text",
                defaultValue: null,
                allowNull: true,
                autoFilled: true,
                props: {
                  width: "7vw",
                  justify: "right",
                },
                // visibility: {
                //   type: "AND",
                //   filters: [
                //     {
                //       type: "IN",
                //       field: "autoGP",
                //       values: [true],
                //     },
                //     {
                //       type: "IN",
                //       field: "transactionType",
                //       values: ["IS", "CS", "PS"],
                //     },
                //   ],
                // },
              },
            ],
          },
          {
            name: "fileInfos",
            label: "File Infos",
            type: "attachment",
            allowUpdate: true,
            allowNull: true,
            props: {
              width: "25vw",
            },
          },
        ],
      },
    ],
  },
  // {
  //   name: "sectionB",
  //   type: "formSection",
  //   alignment: "row",
  //   props: {
  //     width: "25vw",
  //     spacing: "2vw",
  //   },
  //   childFields: [
  //     {
  //       name: "section1",
  //       type: "formSection",
  //       alignment: "column",
  //       props: {
  //         width: "25vw",
  //         spacing: "0.75vw",
  //         justify: "top",
  //       },
  //       childFields: [],
  //     },
  //   ],
  // },
];

export const storeTransferColumns = [
  {
    name: "id",
    label: "MOA No",
    type: "text",
  },
  {
    name: "docDate",
    label: "MOA Date",
    type: "date",
    allowNull: true,
    format: (value) => formatDate(value),
  },
  {
    name: "docLocatorCode",
    label: "Out Locator",
    type: "text",
  },
  {
    name: "docLocatorType",
    label: "Loc",
    type: "text",
  },
  {
    name: "docLocatorName",
    label: "Sender Locator Name",
    type: "text",
  },
  {
    name: "transactionType",
    label: "Transaction",
    allowNull: true,
    type: "text",
    format: (value) => {
      const transactionType = transferTransactionTypeValues.find((item) => item.value === value);
      return transactionType ? transactionType.label : "Unknown";
    },
  },
  // {
  //   name: "docMaterialType",
  //   label: "Mat Type",
  //   allowNull: true,
  //   type: "text",
  //   format: (value) => {
  //     const materialType = materialTypeValues.find((item) => item.value === value);
  //     return materialType ? materialType.label : "Unknown";
  //   },
  // },
  {
    name: "receiverCompanyName",
    label: "Recv Company",
    type: "text",
  },
  {
    name: "receiverLocatorName",
    label: "Recv Locator",
    type: "text",
  },
  {
    name: "receiverProjectCode",
    label: "Recv Project",
    type: "text",
  },
  {
    name: "totalValue",
    label: "Total Value",
    type: "number",
  },
  {
    name: "cancelled",
    label: "Cancel",
    type: "text",
    format: (value) => (value ? "True" : "False"),
  },
  // {
  //   name: "submit",
  //   label: "Submit",
  //   type: "text",
  //   format: (value) => (value ? "True" : "False"),
  // },
  {
    name: "gpFlg",
    label: "GP Made",
    type: "text",
    format: (value) => (value ? "True" : "False"),
  },
];

export const storeTransferSearchColumns = [
  {
    name: "materialOutwardMst.moaId",
    label: "MOA No",
    type: "text",
    defaultValue: null,
    props: {
      widthFactor: 0.8,
    },
  },
  {
    name: "docDate",
    label: "MOA Date",
    type: "date",
    defaultValue: null,
  },
  {
    name: "materialOutwardMst.docLocatorCode",
    label: "Out Locator",
    type: "text",
    defaultValue: null,
  },
  {
    name: "docLocator.locatorName",
    label: "Locator Name",
    foreignEntityType: "docLocator",
    type: "text",
  },
  {
    name: "transactionType",
    label: "Transaction",
    type: "picklist",
    defaultValue: null,
    values: transferTransactionTypeValues,
  },
  // {
  //   name: "docMaterialType",
  //   label: "Mat Type",
  //   type: "picklist",
  //   defaultValue: null,
  //   values: materialTypeValues,
  // },
  {
    name: "companyName",
    label: "Recv Company",
    type: "text",
    foreignEntityType: "receiverCompany",
    defaultValue: null,
  },
  {
    name: "receiverLocator.locatorName",
    label: "Recv Locator",
    type: "text",
    foreignEntityType: "receiverLocator",
    defaultValue: null,
  },
  {
    name: "materialOutwardMst.receiverProjectCode",
    label: "Recv Project",
    type: "text",
    defaultValue: null,
    props: {
      widthFactor: 0.8,
    },
  },
  {
    name: "cancelled",
    label: "Cancel",
    type: "picklist",
    defaultValue: null,
    values: [
      { value: true, label: "True" },
      { value: false, label: "False" },
    ],
    props: {
      widthFactor: 0.6,
    },
  },
  // {
  //   name: "submit",
  //   label: "Submit",
  //   type: "picklist",
  //   defaultValue: null,
  //   values: [
  //     { value: true, label: "True" },
  //     { value: false, label: "False" },
  //   ],
  //   props: {
  //     widthFactor: 0.6,
  //   },
  // },
  {
    name: "gpFlg",
    label: "GP Made",
    type: "picklist",
    defaultValue: null,
    values: [
      { value: true, label: "True" },
      { value: false, label: "False" },
    ],
    props: {
      widthFactor: 0.6,
    },
  },
];

export const storeTransferExcelColumns: Column[] = [
  {
    name: "id",
    label: "MOA No",
    type: "text",
  },
  {
    name: "docDate",
    label: "MOA Date",
    type: "date",
  },
  {
    name: "docLocatorCode",
    label: "Out Locator",
    type: "text",
  },
  {
    name: "docLocatorName",
    label: "Out Locator Name",
    type: "text",
  },
  {
    name: "docStationName",
    label: "Out Station",
    type: "text",
  },
  {
    name: "docStateName",
    label: "Out State",
    type: "text",
  },
  {
    name: "docProjectType",
    label: "Project Type",
    type: "picklist",
    values: outProjectTypeValues,
  },
  {
    name: "docOwnerCode",
    label: "Owner Code",
    type: "text",
  },
  {
    name: "docOwnerName",
    label: "Owner Name",
    type: "text",
  },
  {
    name: "docMaterialType",
    label: "Out Mat",
    type: "picklist",
    values: materialTypeValues,
  },
  {
    name: "docProjectCode",
    label: "Out Project",
    type: "text",
  },
  {
    name: "docProjectName",
    label: "Out Project Name",
    type: "text",
  },
  {
    name: "docVirtualLocator",
    label: "Out Virtual Locator",
    type: "text",
  },
  {
    name: "docCompanyCode",
    label: "Out Company",
    type: "text",
  },
  {
    name: "docCompanyName",
    label: "Out Company Name",
    type: "text",
  },
  {
    name: "senderDocType",
    label: "Sender Doc Type",
    type: "picklist",
    values: senderDocTypeValues,
  },
  {
    name: "senderDocNo",
    label: "Sender Doc No",
    type: "text",
  },
  {
    name: "senderDocDate",
    label: "Sender Doc Date",
    type: "date",
  },
  {
    name: "transactionType",
    label: "Transaction",
    type: "picklist",
    values: transferTransactionTypeValues,
  },
  {
    name: "projectShiftFlg",
    label: "Proj Shift",
    type: "checkbox",
  },
  {
    name: "multiProjectFlg",
    label: "Multi Proj",
    type: "checkbox",
  },
  {
    name: "receiverMaterialType",
    label: "Recv Mat",
    type: "picklist",
    values: materialTypeValues,
  },
  {
    name: "receiverProjectCode",
    label: "Recv Project",
    type: "text",
  },
  {
    name: "receiverProjectName",
    label: "Recv Project Name",
    type: "text",
  },
  {
    name: "receiverVirtualLocator",
    label: "Recv Virtual Locator",
    type: "text",
  },
  {
    name: "receiverLocatorCode",
    label: "Recv Locator",
    type: "text",
  },
  {
    name: "receiverLocatorName",
    label: "Recv Locator Name",
    type: "text",
  },
  // {
  //   name: "receiverAddress1",
  //   label: "Address1",
  //   type: "text",
  // },
  // {
  //   name: "receiverAddress2",
  //   label: "Address2",
  //   type: "text",
  // },
  {
    name: "receiverStationName",
    label: "Recv Station",
    type: "text",
  },
  {
    name: "receiverStateName",
    label: "Recv State",
    type: "text",
  },
  {
    name: "receiverCompanyCode",
    label: "Recv Company",
    type: "text",
  },
  {
    name: "receiverCompanyName",
    label: "Recv Company Name",
    type: "text",
  },
  {
    name: "receiverDocType",
    label: "Receiver Doc Type",
    type: "picklist",
    values: receiverDocTypeValues,
  },
  {
    name: "receiverDocNo",
    label: "Receiver Doc No",
    type: "text",
  },
  {
    name: "receiverDocDate",
    label: "Receiver Doc Date",
    type: "date",
  },
  {
    name: "totalValue",
    label: "Total Value",
    type: "number",
  },
  {
    name: "remarks",
    label: "Remarks",
    type: "text",
  },
  {
    name: "cancelled",
    label: "Cancelled",
    type: "checkbox",
  },
  {
    name: "submit",
    label: "Submit",
    type: "checkbox",
  },
  {
    name: "gpFlg",
    label: "GP Made",
    type: "checkbox",
  },
  {
    name: "gpId",
    label: "GP No",
    type: "number",
  },
  {
    name: "gpDate",
    label: "GP Date",
    type: "date",
  },
  {
    name: "mirId",
    label: "MIR No",
    type: "number",
  },
];

export const storeTransferDetailExcelColumns: Column[] = [
  {
    name: "moaId",
    label: "MOA No",
    type: "text",
  },
  {
    name: "docDate",
    label: "MOA Date",
    type: "date",
  },
  {
    name: "docLocatorCode",
    label: "Out Locator",
    type: "text",
  },
  {
    name: "docLocatorName",
    label: "Out Locator Name",
    type: "text",
  },
  {
    name: "docStationName",
    label: "Out Station",
    type: "text",
  },
  {
    name: "docStateName",
    label: "Out State",
    type: "text",
  },
  {
    name: "docProjectType",
    label: "Project Type",
    type: "picklist",
    values: outProjectTypeValues,
  },
  {
    name: "docOwnerCode",
    label: "Owner Code",
    type: "text",
  },
  {
    name: "docOwnerName",
    label: "Owner Name",
    type: "text",
  },
  {
    name: "docMaterialType",
    label: "Out Mat",
    type: "picklist",
    values: materialTypeValues,
  },
  {
    name: "docProjectCode",
    label: "Out Project",
    type: "text",
  },
  {
    name: "docProjectName",
    label: "Out Project Name",
    type: "text",
  },
  {
    name: "docVirtualLocator",
    label: "Out Virtual Locator",
    type: "text",
  },
  {
    name: "docCompanyCode",
    label: "Out Company",
    type: "text",
  },
  {
    name: "docCompanyName",
    label: "Out Company Name",
    type: "text",
  },
  {
    name: "senderDocType",
    label: "Sender Doc Type",
    type: "picklist",
    values: senderDocTypeValues,
  },
  {
    name: "senderDocNo",
    label: "Sender Doc No",
    type: "text",
  },
  {
    name: "senderDocDate",
    label: "Sender Doc Date",
    type: "date",
  },
  {
    name: "transactionType",
    label: "Transaction",
    type: "picklist",
    values: transferTransactionTypeValues,
  },
  {
    name: "projectShiftFlg",
    label: "Proj Shift",
    type: "checkbox",
  },
  {
    name: "multiProjectFlg",
    label: "Multi Proj",
    type: "checkbox",
  },
  {
    name: "receiverMaterialType",
    label: "Recv Mat",
    type: "picklist",
    values: materialTypeValues,
  },
  {
    name: "receiverProjectCode",
    label: "Recv Project",
    type: "text",
  },
  {
    name: "receiverProjectName",
    label: "Recv Project Name",
    type: "text",
  },
  {
    name: "receiverVirtualLocator",
    label: "Recv Virtual Locator",
    type: "text",
  },
  {
    name: "receiverLocatorCode",
    label: "Recv Locator",
    type: "text",
  },
  {
    name: "receiverLocatorName",
    label: "Recv Locator Name",
    type: "text",
  },
  // {
  //   name: "receiverAddress1",
  //   label: "Address1",
  //   type: "text",
  // },
  // {
  //   name: "receiverAddress2",
  //   label: "Address2",
  //   type: "text",
  // },
  {
    name: "receiverStationName",
    label: "Recv Station",
    type: "text",
  },
  {
    name: "receiverStateName",
    label: "Recv State",
    type: "text",
  },
  {
    name: "receiverCompanyCode",
    label: "Recv Company",
    type: "text",
  },
  {
    name: "receiverCompanyName",
    label: "Recv Company Name",
    type: "text",
  },
  {
    name: "receiverDocType",
    label: "Receiver Doc Type",
    type: "picklist",
    values: receiverDocTypeValues,
  },
  {
    name: "receiverDocNo",
    label: "Receiver Doc No",
    type: "text",
  },
  {
    name: "receiverDocDate",
    label: "Receiver Doc Date",
    type: "date",
  },
  {
    name: "totalValue",
    label: "Total Value",
    type: "number",
  },
  {
    name: "id",
    label: "Row Id",
    type: "text",
  },
  {
    name: "itemName",
    label: "Item Name",
    type: "text",
  },
  {
    name: "itemId",
    label: "Item Id",
    type: "text",
  },
  {
    name: "itemCustCode",
    label: "Part Code",
    type: "text",
  },
  {
    name: "uom",
    label: "Unit",
    type: "text",
  },
  {
    name: "projectCode",
    label: "Project",
    type: "text",
  },
  {
    name: "projectName",
    label: "Project Name",
    type: "text",
  },
  {
    name: "projectVirtualLocator",
    label: "Virtual Locator",
    type: "text",
  },
  {
    name: "totalQuantity",
    label: "Out Quantity",
    type: "number",
  },
  {
    name: "rate",
    label: "Rate",
    type: "number",
  },
  {
    name: "totalAmount",
    label: "Total Amount",
    type: "number",
  },
  {
    name: "cancelled",
    label: "Cancelled",
    type: "checkbox",
  },
  {
    name: "submit",
    label: "Submit",
    type: "checkbox",
  },
  {
    name: "gpFlg",
    label: "GP Made",
    type: "checkbox",
  },
];
